import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Truck, Zap, MapPin, Fuel, Gauge, Clock } from "lucide-react";
import { toast } from "sonner";

const scoreTone = (s) => s >= 100 ? "text-emerald-500" : s >= 40 ? "text-primary-orange" : s >= 0 ? "text-amber-500" : "text-red-500";

export default function AIOptimiserDialog({ open, onOpenChange, jobId, onAssign }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(null);

  const load = async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const r = await api.post("/dispatch/optimise", { job_id: jobId });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Optimiser failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open && jobId) load(); /* eslint-disable-next-line */ }, [open, jobId]);

  const assign = async (truckId) => {
    setAssigning(truckId);
    try {
      await api.post(`/jobs/${jobId}/assign-truck`, { truck_id: truckId });
      toast.success("Truck assigned");
      onOpenChange(false);
      onAssign?.(truckId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assign failed");
    } finally { setAssigning(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-industrial max-w-2xl" data-testid="ai-optimiser-dialog">
        <DialogHeader>
          <DialogTitle className="heading uppercase flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-primary-orange" /> AI Dispatch Optimiser
          </DialogTitle>
        </DialogHeader>

        {loading && <div className="label-tag text-primary-orange pulse-orange">Scoring every truck…</div>}

        {data && (
          <>
            <div className="text-xs text-muted-industrial mb-3">
              <span className="mono">{data.job_customer}</span> · {data.job_site || "—"} ·{" "}
              <span className="text-primary-orange mono">{data.remaining_m3} m³ remaining</span>
            </div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {data.suggestions.length === 0 && <div className="text-sm text-muted-industrial">No trucks in this plant.</div>}
              {data.suggestions.map((s) => (
                <div
                  key={s.truck_id}
                  data-testid={`optimise-suggestion-${s.truck_number}`}
                  className={`border rounded-lg p-3 ${s.recommended ? "border-primary-orange bg-primary-orange/5" : "border-industrial"}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-lg bg-surface-2 border border-industrial flex items-center justify-center shrink-0">
                        <Truck className="w-5 h-5 text-primary-orange" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="heading font-black text-base">{s.truck_number}</span>
                          {s.recommended && (
                            <span className="mono text-[9px] uppercase tracking-widest px-2 py-0.5 bg-primary-orange text-black rounded-md font-bold inline-flex items-center gap-1">
                              <Zap className="w-2.5 h-2.5" /> Best fit
                            </span>
                          )}
                          <span className="mono text-[10px] uppercase text-muted-industrial">{s.status?.replace("_", " ")}</span>
                        </div>
                        <div className="text-xs text-muted-industrial mt-0.5 mono truncate">
                          {s.driver_name} · {s.rego || "—"} · {s.capacity_m3} m³
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-[11px] mono text-muted-industrial">
                          {s.distance_km != null && (<span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.distance_km} km</span>)}
                          {s.eta_min != null && (<span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {s.eta_min} min ETA</span>)}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {s.reasons.map((r, i) => (
                            <span key={i} className="mono text-[10px] px-1.5 py-0.5 bg-surface-2 border border-industrial rounded text-muted-industrial">{r}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`mono text-2xl font-black leading-none ${scoreTone(s.score)}`}>{s.score}</div>
                      <div className="mono text-[9px] uppercase text-muted-industrial mt-1">SCORE</div>
                      <Button
                        onClick={() => assign(s.truck_id)}
                        disabled={assigning === s.truck_id}
                        data-testid={`assign-${s.truck_number}`}
                        className="btn-cta mt-2 h-8 text-[10px] px-3"
                      >
                        {assigning === s.truck_id ? "…" : "Assign"}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
